import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  LinearProgress,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Email,
  Phone,
  Work,
  CalendarToday,
  AttachMoney,
  Star,
  Badge,
  Close,
  Edit,
  TrendingUp,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { Staff, staffService } from '../../services/staffService';
import { useAppDispatch } from '../../hooks/redux';
import { addNotification } from '../../store/slices/uiSlice';

interface StaffDetailsProps {
  open: boolean;
  onClose: () => void;
  staff: Staff | null;
  onEdit?: (staff: Staff) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`staff-tabpanel-${index}`}
      aria-labelledby={`staff-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const StaffDetails: React.FC<StaffDetailsProps> = ({
  open,
  onClose,
  staff,
  onEdit,
}) => {
  const dispatch = useAppDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [performance, setPerformance] = useState<any[]>([]);
  const [loadingPerformance, setLoadingPerformance] = useState(false);

  useEffect(() => {
    if (staff && open) {
      fetchPerformance();
    }
  }, [staff, open]);

  const fetchPerformance = async () => {
    if (!staff) return;
    
    setLoadingPerformance(true);
    try {
      const performanceData = await staffService.getStaffPerformance(staff.id);
      setPerformance(performanceData);
    } catch (error) {
      console.error('Error fetching performance:', error);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to fetch performance data',
      }));
    } finally {
      setLoadingPerformance(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return 'Not set';
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'SALON_OWNER':
        return '#E8B4B8';
      case 'MANAGER':
        return '#8B5CF6';
      case 'STAFF':
        return '#06B6D4';
      case 'RECEPTIONIST':
        return '#10B981';
      default:
        return '#6B7280';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'SALON_OWNER':
        return 'Salon Owner';
      case 'MANAGER':
        return 'Manager';
      case 'STAFF':
        return 'Staff';
      case 'RECEPTIONIST':
        return 'Receptionist';
      default:
        return role;
    }
  };

  if (!staff) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle 
        sx={{ 
          background: 'linear-gradient(135deg, #E8B4B8 0%, #F5D5D8 100%)',
          color: 'white',
          fontWeight: 800,
          fontSize: '1.5rem',
          textAlign: 'center',
          py: 3,
          position: 'relative',
        }}
      >
        Staff Details
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Staff Header */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            bgcolor: 'rgba(232, 180, 184, 0.05)',
            borderRadius: 0,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: getRoleColor(staff.user.role),
                  fontSize: '2rem',
                  fontWeight: 700,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                }}
              >
                {getInitials(staff.firstName, staff.lastName)}
              </Avatar>
            </Grid>
            <Grid item xs>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {staff.firstName} {staff.lastName}
                </Typography>
                <Chip
                  label={getRoleLabel(staff.user.role)}
                  sx={{
                    bgcolor: getRoleColor(staff.user.role),
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                />
                <Chip
                  label={staff.isActive ? 'Active' : 'Inactive'}
                  color={staff.isActive ? 'success' : 'default'}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                {staff.position} • {staff.department}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Employee ID: {staff.employeeId}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {staff.skills.slice(0, 3).map((skill, index) => (
                  <Chip
                    key={index}
                    label={skill}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: '#E8B4B8',
                      color: '#E8B4B8',
                      fontWeight: 500,
                    }}
                  />
                ))}
                {staff.skills.length > 3 && (
                  <Chip
                    label={`+${staff.skills.length - 3} more`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: '#E8B4B8',
                      color: '#E8B4B8',
                      fontWeight: 500,
                    }}
                  />
                )}
              </Box>
            </Grid>
            <Grid item>
              {onEdit && (
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  onClick={() => onEdit(staff)}
                  sx={{
                    bgcolor: '#E8B4B8',
                    fontWeight: 700,
                    px: 3,
                    py: 1.5,
                    borderRadius: 3,
                    '&:hover': {
                      bgcolor: '#D4A4A8',
                    },
                  }}
                >
                  Edit Profile
                </Button>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                minHeight: 64,
                '&.Mui-selected': {
                  color: '#E8B4B8',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#E8B4B8',
                height: 3,
              },
            }}
          >
            <Tab label="Personal Info" />
            <Tab label="Work Schedule" />
            <Tab label="Performance" />
            <Tab label="Certifications" />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Contact Information */}
            <Grid item xs={12} md={6}>
              <Card 
                elevation={0}
                sx={{ 
                  bgcolor: 'rgba(232, 180, 184, 0.05)',
                  borderRadius: 3,
                  border: '1px solid rgba(232, 180, 184, 0.1)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#E8B4B8' }}>
                    Contact Information
                  </Typography>
                  <List sx={{ p: 0 }}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Email sx={{ color: '#E8B4B8' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Email"
                        secondary={staff.email}
                        secondaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Phone sx={{ color: '#E8B4B8' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Phone"
                        secondary={staff.phone}
                        secondaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <CalendarToday sx={{ color: '#E8B4B8' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Date of Birth"
                        secondary={staff.dateOfBirth ? formatDate(staff.dateOfBirth) : 'Not provided'}
                        secondaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Employment Details */}
            <Grid item xs={12} md={6}>
              <Card 
                elevation={0}
                sx={{ 
                  bgcolor: 'rgba(232, 180, 184, 0.05)',
                  borderRadius: 3,
                  border: '1px solid rgba(232, 180, 184, 0.1)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#E8B4B8' }}>
                    Employment Details
                  </Typography>
                  <List sx={{ p: 0 }}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Work sx={{ color: '#E8B4B8' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Position"
                        secondary={staff.position}
                        secondaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Badge sx={{ color: '#E8B4B8' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Department"
                        secondary={staff.department}
                        secondaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <CalendarToday sx={{ color: '#E8B4B8' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Hire Date"
                        secondary={formatDate(staff.hireDate)}
                        secondaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Compensation */}
            <Grid item xs={12} md={6}>
              <Card 
                elevation={0}
                sx={{ 
                  bgcolor: 'rgba(232, 180, 184, 0.05)',
                  borderRadius: 3,
                  border: '1px solid rgba(232, 180, 184, 0.1)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#E8B4B8' }}>
                    Compensation
                  </Typography>
                  <List sx={{ p: 0 }}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <AttachMoney sx={{ color: '#E8B4B8' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Hourly Rate"
                        secondary={staff.hourlyRate ? `₹${staff.hourlyRate}/hour` : 'Not set'}
                        secondaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <TrendingUp sx={{ color: '#E8B4B8' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Commission Rate"
                        secondary={staff.commissionRate ? `${staff.commissionRate}%` : 'Not set'}
                        secondaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Skills */}
            <Grid item xs={12} md={6}>
              <Card 
                elevation={0}
                sx={{ 
                  bgcolor: 'rgba(232, 180, 184, 0.05)',
                  borderRadius: 3,
                  border: '1px solid rgba(232, 180, 184, 0.1)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#E8B4B8' }}>
                    Skills & Expertise
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {staff.skills.map((skill, index) => (
                      <Chip
                        key={index}
                        label={skill}
                        variant="outlined"
                        sx={{
                          borderColor: '#E8B4B8',
                          color: '#E8B4B8',
                          fontWeight: 500,
                          '&:hover': {
                            bgcolor: 'rgba(232, 180, 184, 0.1)',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Card 
            elevation={0}
            sx={{ 
              bgcolor: 'rgba(232, 180, 184, 0.05)',
              borderRadius: 3,
              border: '1px solid rgba(232, 180, 184, 0.1)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#E8B4B8' }}>
                Weekly Schedule
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(staff.workingHours).map(([day, hours]) => (
                  <Grid item xs={12} sm={6} md={4} key={day}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 2, 
                        bgcolor: hours.isWorking ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        border: `1px solid ${hours.isWorking ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                        borderRadius: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {hours.isWorking ? (
                          <CheckCircle sx={{ color: '#10B981', fontSize: 20 }} />
                        ) : (
                          <Cancel sx={{ color: '#EF4444', fontSize: 20 }} />
                        )}
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                          {day}
                        </Typography>
                      </Box>
                      {hours.isWorking ? (
                        <Typography variant="body2" color="text.secondary">
                          {formatTime(hours.start)} - {formatTime(hours.end)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Day off
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Card 
            elevation={0}
            sx={{ 
              bgcolor: 'rgba(232, 180, 184, 0.05)',
              borderRadius: 3,
              border: '1px solid rgba(232, 180, 184, 0.1)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#E8B4B8' }}>
                Performance Metrics
              </Typography>
              {loadingPerformance ? (
                <Box sx={{ py: 4 }}>
                  <LinearProgress sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Loading performance data...
                  </Typography>
                </Box>
              ) : performance.length > 0 ? (
                <Grid container spacing={3}>
                  {performance.map((metric, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Paper 
                        elevation={0}
                        sx={{ 
                          p: 3, 
                          bgcolor: 'white',
                          border: '1px solid rgba(232, 180, 184, 0.2)',
                          borderRadius: 2,
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#E8B4B8', mb: 1 }}>
                          {metric.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {metric.label}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  No performance data available yet. Performance metrics will be displayed once the staff member starts working.
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Card 
            elevation={0}
            sx={{ 
              bgcolor: 'rgba(232, 180, 184, 0.05)',
              borderRadius: 3,
              border: '1px solid rgba(232, 180, 184, 0.1)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#E8B4B8' }}>
                Certifications & Qualifications
              </Typography>
              {staff.certifications && staff.certifications.length > 0 ? (
                <Grid container spacing={2}>
                  {staff.certifications.map((cert, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Paper 
                        elevation={0}
                        sx={{ 
                          p: 3, 
                          bgcolor: 'white',
                          border: '1px solid rgba(232, 180, 184, 0.2)',
                          borderRadius: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar sx={{ bgcolor: '#E8B4B8', width: 40, height: 40 }}>
                            <Star />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {typeof cert === 'string' ? cert : cert.name}
                            </Typography>
                            {typeof cert === 'object' && cert.issuer && (
                              <Typography variant="body2" color="text.secondary">
                                {cert.issuer}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        {typeof cert === 'object' && cert.year && (
                          <Typography variant="body2" color="text.secondary">
                            Obtained: {cert.year}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  No certifications recorded yet.
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button 
          onClick={onClose}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 3,
            fontWeight: 600,
            color: '#666',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StaffDetails;